import { websiteLocaleOf } from '@affine/env/website';
import type { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  CloudBubbleIcon,
  ContactWithUsIcon,
  HelpCenterIcon,
  HelpIcon,
  NewIcon,
} from '@blocksuite/icons/rc';

import type { WorkspaceDialogService } from '../modules/dialogs';
import type { HelpActionsService } from '../modules/help-actions';
import { registerAffineCommand } from './registry';

export function registerAffineHelpCommands({
  t,
  helpActionsService,
  workspaceDialogService,
}: {
  t: ReturnType<typeof useI18n>;
  helpActionsService: HelpActionsService;
  workspaceDialogService: WorkspaceDialogService;
}) {
  const unsubs: Array<() => void> = [];
  const locale = websiteLocaleOf(t.language);
  unsubs.push(
    registerAffineCommand({
      id: 'affine:help-whats-new',
      category: 'affine:help',
      icon: <NewIcon />,
      label: t['com.streamlain.cmdk.affine.whats-new'](),
      run() {
        track.$.cmdk.help.openChangelog();
        helpActionsService.openReleaseNotes(locale);
      },
    })
  );
  unsubs.push(
    registerAffineCommand({
      id: 'affine:help-report-bug',
      category: 'affine:help',
      icon: <ContactWithUsIcon />,
      label: t['com.streamlain.help.report-bug'](),
      run() {
        helpActionsService.reportBug();
      },
    }),
    registerAffineCommand({
      id: 'affine:help-submit-idea',
      category: 'affine:help',
      icon: <HelpIcon />,
      label: t['com.streamlain.help.submit-idea'](),
      run() {
        helpActionsService.submitIdea();
      },
    }),
    registerAffineCommand({
      id: 'affine:help-system-status',
      category: 'affine:help',
      icon: <CloudBubbleIcon />,
      label: t['com.streamlain.help.system-status'](),
      run() {
        helpActionsService.openStatus(locale);
      },
    }),
    registerAffineCommand({
      id: 'affine:help-platform-docs',
      category: 'affine:help',
      icon: <HelpCenterIcon />,
      label: t['com.streamlain.help.platform-docs'](),
      run() {
        helpActionsService.openPlatformDocs(locale);
      },
    })
  );
  unsubs.push(
    registerAffineCommand({
      id: 'affine:help-contact-us',
      category: 'affine:help',
      icon: <ContactWithUsIcon />,
      label: t['com.streamlain.cmdk.affine.contact-us'](),
      run() {
        track.$.cmdk.help.contactUs();
        workspaceDialogService.open('setting', {
          activeTab: 'about',
        });
      },
    })
  );

  return () => {
    unsubs.forEach(unsub => unsub());
  };
}
